import { createSlice } from "@reduxjs/toolkit";
import { rgsPlay, holdAndWinSetSpinComplete } from "./spinSlice";
import { initializeGame } from "./gameSettingsSlice";
import { setSceneTransitionActive } from "./animationsSlice";
import { openHoldAndWinResumePopup } from "./userInterfaceSlice";

// Initial state for feature game settings
const initialState = {
  holdAndWinActive: false, // Track if hold and win feature game is currently active
  respinsLeft: 0, // Track the number of respins left (displayed value)
  pendingRespinsLeft: null, // Store pending respins from API (updated after spin completes)
  initialReels: [], // Initial reels from trigger round (set once, never changes during feature)
  currentRound: {
    reels: [],
    wins: [],
    win: 0,
    storage: null, // Store coin_multipliers, progress_bar, etc.
  },
  progressBarCount: 0, // Track total coins collected for progress bar
  holdAndWinSymbolsReady: false, // Track if all Hold and Win symbols are initialized and ready
  readySymbolsCount: 0, // Count of symbols that have reported ready (out of 20 total: 5 reels × 4 symbols)
  holdAndWinReelsReady: false, // Track if all Hold and Win base reels have exposed their refs via imperative handles
  readyReelsCount: 0, // Count of base reels that have reported ready (out of 5 total reels)
  holdAndWinOverlayReelsReady: false, // Track if all Hold and Win overlay reels have exposed their refs
  readyOverlayReelsCount: 0, // Count of overlay reels that have reported ready (out of 5 total overlay reels)
  isHoldAndWinIntroReload: false, // Flag: reloading right after trigger (before any respins)
  isHoldAndWinMidFeatureReload: false, // Flag: reloading mid-feature (after at least one respin)
};

// Feature game settings slice
const featureGameSettingsSlice = createSlice({
  name: "featureGameSettings",
  initialState,
  reducers: {
    // Set hold and win active state
    setHoldAndWinActive: (state, action) => {
      state.holdAndWinActive = action.payload;
      // Reset pending respins when HoldAndWin becomes inactive
      if (!action.payload) {
        state.pendingRespinsLeft = null;
      }
    },
    setRespinsLeft: (state, action) => {
      state.respinsLeft = action.payload;
    },
    setProgressBarCount: (state, action) => {
      state.progressBarCount = action.payload;
    },
    // Report that a symbol is ready
    reportSymbolReady: (state) => {
      state.readySymbolsCount += 1;

      // All 20 symbols ready (5 reels × 4 symbols)
      if (state.readySymbolsCount >= 20) {
        state.holdAndWinSymbolsReady = true;
      }
    },
    // Report that a base reel's imperative handle is ready
    reportReelReady: (state) => {
      state.readyReelsCount += 1;

      // All 5 base reels ready
      if (state.readyReelsCount >= 5) {
        state.holdAndWinReelsReady = true;
      }
    },
    // Report that an overlay reel's imperative handle is ready
    reportOverlayReelReady: (state) => {
      state.readyOverlayReelsCount += 1;

      // All 5 overlay reels ready
      if (state.readyOverlayReelsCount >= 5) {
        state.holdAndWinOverlayReelsReady = true;
      }
    },
    // Reset symbol and reel readiness (when Hold and Win deactivates or game reloads)
    resetSymbolReadiness: (state) => {
      state.holdAndWinSymbolsReady = false;
      state.readySymbolsCount = 0;
      state.holdAndWinReelsReady = false;
      state.readyReelsCount = 0;
      state.holdAndWinOverlayReelsReady = false;
      state.readyOverlayReelsCount = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle Hold and Win reload from initializeGame
      .addCase(initializeGame.fulfilled, (state, action) => {
        const { result } = action.payload;

        // Handle intro reload: Right after trigger (before first respin)
        if (result.isHoldAndWinIntroReload) {
          // Stay in base game mode - intro dialog will show and transition normally
          state.holdAndWinActive = false;
          state.isHoldAndWinIntroReload = true;
          state.isHoldAndWinMidFeatureReload = false;

          // CRITICAL: Store respins and progress bar for when intro closes
          if (result.features?.respins_left !== undefined) {
            state.respinsLeft = result.features.respins_left;
          }
          if (result.holdAndWinStorage?.progress_bar !== undefined) {
            state.progressBarCount = result.holdAndWinStorage.progress_bar;
          }

          // CRITICAL: Store transformed reels (coins + blanks) for Hold and Win rendering
          if (result.holdAndWinInitialReels) {
            state.initialReels = result.holdAndWinInitialReels;
          }
          if (result.holdAndWinRound) {
            state.currentRound = result.holdAndWinRound;
          }
        }
        // Handle mid-feature reload: After at least one respin
        else if (result.isHoldAndWinMidFeatureReload) {
          // Set respins from features
          state.respinsLeft = result.features.respins_left;
          state.pendingRespinsLeft = null;

          // Set initial reels from Hold and Win data (current screen state)
          if (result.holdAndWinInitialReels) {
            state.initialReels = result.holdAndWinInitialReels;
          }

          // Set current round with storage data
          if (result.holdAndWinRound) {
            state.currentRound = result.holdAndWinRound;
          }

          // Set progress bar count from storage
          if (result.holdAndWinStorage?.progress_bar !== undefined) {
            state.progressBarCount = result.holdAndWinStorage.progress_bar;
          }

          // Activate Hold and Win mode immediately
          state.holdAndWinActive = true;
          state.isHoldAndWinIntroReload = false;
          state.isHoldAndWinMidFeatureReload = true;

          // Reset symbol and reel readiness - they will report when ready
          state.holdAndWinSymbolsReady = false;
          state.readySymbolsCount = 0;
          state.holdAndWinReelsReady = false;
          state.readyReelsCount = 0;
          state.holdAndWinOverlayReelsReady = false;
          state.readyOverlayReelsCount = 0;
        } else {
          // Normal base game - ensure Hold and Win is inactive
          state.holdAndWinActive = false;
          state.respinsLeft = 0;
          state.initialReels = [];
          state.currentRound = {
            reels: [],
            wins: [],
            win: 0,
            storage: null,
          };
          state.progressBarCount = 0;
          state.holdAndWinSymbolsReady = false;
          state.readySymbolsCount = 0;
          state.holdAndWinReelsReady = false;
          state.readyReelsCount = 0;
          state.isHoldAndWinIntroReload = false;
          state.isHoldAndWinMidFeatureReload = false;
          state.holdAndWinOverlayReelsReady = false;
          state.readyOverlayReelsCount = 0;
        }
      })
      // Decrease respins immediately when HoldAndWin spin starts
      .addCase(rgsPlay.pending, (state) => {
        // Only decrease if HoldAndWin is active and we have respins left
        if (state.holdAndWinActive && state.respinsLeft > 0) {
          // Immediately decrease respins (user paid for this spin)
          state.respinsLeft -= 1;
        }
      })
      .addCase(rgsPlay.fulfilled, (state, action) => {
        const { holdAndWinRound, holdAndWinInitialReels, features } =
          action.payload;

        // Store initial reels if provided (trigger round)
        if (holdAndWinInitialReels) {
          state.initialReels = holdAndWinInitialReels;

          // Trigger round: Set initial respins value immediately (this is the starting value)
          if (features && features.respins_left !== undefined) {
            state.respinsLeft = features.respins_left;
            state.pendingRespinsLeft = null; // No pending value for trigger round
          }
        }

        if (holdAndWinRound) {
          // HoldAndWin round: Store round
          state.currentRound = holdAndWinRound;

          // For subsequent respins (not trigger round): Store pending respins value
          // (will update display after spin completes)
          if (
            !holdAndWinInitialReels &&
            features &&
            features.respins_left !== undefined
          ) {
            state.pendingRespinsLeft = features.respins_left;
          }
        } else {
          // Regular spin: Reset everything to empty/default
          state.initialReels = [];
          state.currentRound = {
            reels: [],
            wins: [],
            win: 0,
          };
          state.pendingRespinsLeft = null;
        }
      })
      // Update respins from pending value when spin completes
      .addCase(holdAndWinSetSpinComplete, (state, action) => {
        // Only update if spin completed (payload === true) and we have pending value
        if (action.payload === true && state.pendingRespinsLeft !== null) {
          state.respinsLeft = state.pendingRespinsLeft;
          state.pendingRespinsLeft = null; // Clear pending value
        }
      })
      // Clear reload flags when scene transition COMPLETES (not when it starts)
      // This ensures flags stay active during transition so animations trigger while screen is black
      .addCase(setSceneTransitionActive, (state, action) => {
        // Only clear when transition is ENDING (false)
        if (action.payload === false) {
          state.isHoldAndWinIntroReload = false;
          state.isHoldAndWinMidFeatureReload = false;
        }
      })
      // Also clear reload flags when Hold and Win reload popup is opened (mid-feature reload path)
      .addCase(openHoldAndWinResumePopup, (state) => {
        state.isHoldAndWinIntroReload = false;
        state.isHoldAndWinMidFeatureReload = false;
      });
  },
});

// Export actions for use in components or other slices
export const {
  setHoldAndWinActive,
  setRespinsLeft,
  setProgressBarCount,
  reportSymbolReady,
  reportReelReady,
  reportOverlayReelReady,
  resetSymbolReadiness,
} = featureGameSettingsSlice.actions;

// Export selectors
export const selectHoldAndWinActive = (state) =>
  state.featureGame.holdAndWinActive;

export const selectHoldAndWinInitialReels = (state) =>
  state.featureGame.initialReels;

export const selectHoldAndWinCurrentRound = (state) =>
  state.featureGame.currentRound;

export const selectHoldAndWinCoinMultipliers = (state) =>
  state.featureGame.currentRound?.storage?.coin_multipliers || null;

export const selectRespinsLeft = (state) => state.featureGame.respinsLeft;

export const selectProgressBarCount = (state) =>
  state.featureGame.progressBarCount;

export const selectHoldAndWinSymbolsReady = (state) =>
  state.featureGame.holdAndWinSymbolsReady;

export const selectHoldAndWinReelsReady = (state) =>
  state.featureGame.holdAndWinReelsReady;

export const selectHoldAndWinOverlayReelsReady = (state) =>
  state.featureGame.holdAndWinOverlayReelsReady;

export const selectIsHoldAndWinIntroReload = (state) =>
  state.featureGame.isHoldAndWinIntroReload;

export const selectIsHoldAndWinMidFeatureReload = (state) =>
  state.featureGame.isHoldAndWinMidFeatureReload;

// Export the reducer to integrate into the Redux store
export default featureGameSettingsSlice.reducer;
